import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type * as monacoTypes from 'monaco-editor';
import { useId, useRef } from 'react';
import { Controller, type ControllerProps, type FieldValues, type Path, type RegisterOptions, useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { JsonEditor, type JsonEditorProps } from '../../../json-editor';
import type { LabelTooltipProps } from '../../internals/form-label';
import FormWrapper from '../../internals/form-wrapper';
export type FormJsonEditorProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  label?: string | React.ReactNode;
  rules?: ControllerProps<TFieldValues>['rules'];
  hint?: string | React.ReactNode;
  className?: string;
  errorPosition?: 'absolute' | 'relative';
  orientation?: 'vertical' | 'horizontal';
  addon?: React.ReactNode;
  tooltip?: LabelTooltipProps;
  disabled?: boolean;
} & Omit<JsonEditorProps, 'value' | 'onChange' | 'isDisabled'>;
export default function FormJsonEditor(t0) {
  const $ = _c(45);
  let addon;
  let className;
  let hint;
  let jsonEditorProps;
  let label;
  let name;
  let propDisabled;
  let rules;
  let t1;
  let t2;
  let tooltip;
  if ($[0] !== t0) {
    ({
      name,
      label,
      rules,
      hint,
      errorPosition: t1,
      orientation: t2,
      addon,
      className,
      tooltip,
      disabled: propDisabled,
      ...jsonEditorProps
    } = t0);
    $[0] = t0;
    $[1] = addon;
    $[2] = className;
    $[3] = hint;
    $[4] = jsonEditorProps;
    $[5] = label;
    $[6] = name;
    $[7] = propDisabled;
    $[8] = rules;
    $[9] = t1;
    $[10] = t2;
    $[11] = tooltip;
  } else {
    addon = $[1];
    className = $[2];
    hint = $[3];
    jsonEditorProps = $[4];
    label = $[5];
    name = $[6];
    propDisabled = $[7];
    rules = $[8];
    t1 = $[9];
    t2 = $[10];
    tooltip = $[11];
  }
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const {
    control,
    setError,
    clearErrors
  } = useFormContext();
  const contextId = useId();
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const validationMarkersRef = useRef(t3);
  let t4;
  if ($[13] !== clearErrors || $[14] !== name || $[15] !== setError) {
    t4 = markers => {
      validationMarkersRef.current = markers;
      if (markers.length > 0) {
        const firstError = markers[0];
        setError(name, {
          type: "validation",
          message: firstError.message
        });
      } else {
        clearErrors(name);
      }
    };
    $[13] = clearErrors;
    $[14] = name;
    $[15] = setError;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handleValidate = t4;
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const errorMarkers = validationMarkersRef.current.filter(_temp);
      if (errorMarkers.length > 0) {
        return errorMarkers[0].message;
      }
      return true;
    };
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== rules) {
    t6 = typeof rules?.validate === "function" ? {
      custom: rules.validate
    } : rules?.validate;
    $[18] = rules;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = {
      monacoValidation: t5,
      ...t6
    };
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== rules || $[23] !== t7) {
    t8 = {
      ...rules,
      validate: t7
    };
    $[22] = rules;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const mergedRules = t8;
  let t9;
  if ($[25] !== jsonEditorProps.autocompleteItems || $[26] !== label) {
    t9 = jsonEditorProps.autocompleteItems ? <div className="flex flex-col gap-1">{label}<p className="text-caption-2 text-tertiary"><Trans ns="ui" i18nKey={_temp2} components={{
          kbd: <kbd className="text-snippet-2" />
        }} /></p></div> : label;
    $[25] = jsonEditorProps.autocompleteItems;
    $[26] = label;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const LabelComponent = t9;
  let t10;
  if ($[28] !== LabelComponent || $[29] !== addon || $[30] !== className || $[31] !== contextId || $[32] !== errorPosition || $[33] !== handleValidate || $[34] !== hint || $[35] !== jsonEditorProps || $[36] !== orientation || $[37] !== propDisabled || $[38] !== tooltip) {
    t10 = t11 => {
      const {
        field,
        fieldState
      } = t11;
      const error = fieldState.error?.message;
      return <FormWrapper id={contextId} error={error} label={LabelComponent} errorPosition={errorPosition} size="default" hint={hint} orientation={orientation} fullWidth={true} disabled={propDisabled || field.disabled} addon={addon} tooltip={tooltip} errorComponent="inlineAlert"><div className={cx({
          "rounded-bl-none! rounded-tl-none!": addon
        }, className)}><JsonEditor {...jsonEditorProps} {...field} isDisabled={field.disabled || propDisabled} onValidate={handleValidate} /></div></FormWrapper>;
    };
    $[28] = LabelComponent;
    $[29] = addon;
    $[30] = className;
    $[31] = contextId;
    $[32] = errorPosition;
    $[33] = handleValidate;
    $[34] = hint;
    $[35] = jsonEditorProps;
    $[36] = orientation;
    $[37] = propDisabled;
    $[38] = tooltip;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== control || $[41] !== mergedRules || $[42] !== name || $[43] !== t10) {
    t11 = <Controller name={name} control={control} rules={mergedRules} render={t10} />;
    $[40] = control;
    $[41] = mergedRules;
    $[42] = name;
    $[43] = t10;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
}
function _temp2($) {
  return $.components.form["json-editor"].sublabel;
}
function _temp(m) {
  return m.severity === 8;
}